<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import EventSearchBar from '@/components/EventSearch/EventSearchBar.vue'
import NewResultsBanner from '@/components/EventSearch/NewResultsBanner.vue'
import ToolSidebar from '@/components/EventSearch/ToolSidebar.vue'
import ExportWarningModal from '@/components/EventSearch/ExportWarningModal.vue'
import Events from '@/api/Events'
import User from '@/api/User'
import SavedSearches from '@/api/SavedSearches'
import { useUserStore } from '@/stores/user'
import { useDataSourcesStore } from '@product/stores/dataSources'
import { useFieldSettings } from '@/composables/useFieldSettings'
import { useGroupMode, isValidGroupMode, DEFAULT_GROUP_MODE } from '@/composables/useGroupMode'
import {
  FILTER_TYPES,
  createTimeFilter,
  createIngestSinceFilter,
  findTimeFilter,
  findIngestFilter,
  buildFinalQuery,
  filterToQueryString,
  buildFilterClause,
  stripClauseFromQuery,
  andJoin
} from '@/components/EventSearch/filters'
import { selectionToFilterParams, extractTimeFromQuery } from '@/components/EventSearch/timeRangeUtils'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const dataSourcesStore = useDataSourcesStore()
const toast = useToast()

// Check if user has access to event search (requires Elite tier or higher)
const hasEventAccess = computed(() => {
  return userStore.accountType >= 4 || userStore.accountType === 1337
})
const forbidden = ref(false)

// The landing page is a self-contained search stage: it carries its own query
// input, source selector and time range, so the toolbar and tool rail would only
// duplicate them. Both come back once a search lands on a results view.
const isLanding = computed(() => route.path === '/search/event')

// Field settings management (visibility + order)
const {
  isFieldEnabled,
  toggleField,
  getFieldOrder,
  setFieldOrder,
  getOrderedEnabledFields,
  resetLogType,
  hasCustomSettings
} = useFieldSettings()

// Field mappings from store (log type -> fields)
const fieldMappings = computed(() => dataSourcesStore.fieldMappings)

// Group mode (shared, persisted) — source mode always wants an activeSource
const { groupMode } = useGroupMode()

// Sidebar state
const toolSidebarExpanded = ref(false)

// Search state - managed here, passed to children via props
const query = ref('')
const lastExecutedQuery = ref('')  // The query that was last executed (stable base for filters)
// Tracks quick filters added by clicking a cell +/- (keyed by the applied item
// set's signature) so a later click on the same value(s) reconciles instead of
// stacking a contradiction. This is a disposable shadow of what was appended —
// the query string stays the source of truth and is cleared on any hand-run
// query (see handleSearch), after which clicks simply append again.
const clickFilters = ref([])  // [{ sig, operator, clause }]
const histogram = ref([])
const logTypeDistribution = ref([])
const sourceLogTypeDistribution = ref([])
const activeSource = ref(null)
const loading = ref(false)
const exportLoading = ref(false)
const error = ref('')

// Export warning dialog state
const CSV_SAFETY_KEY = 'webscout-csv-safety-dismissed'
const PARTIAL_EXPORT_KEY = 'webscout-partial-export-dismissed'
const showCsvWarning = ref(false)
const showPartialWarning = ref(false)
const pendingExport = ref(null) // { type: 'all'|'logType', logType?, format }
const pageSize = ref(Number(localStorage.getItem('eventSearchPageSize')) || 50)

// Filter system state (time filters only — non-time filters are now part of the query string)
const filters = ref([])

// Time range dropdown state (tracks the dropdown selection separately from the filter)
const searchTimeRange = ref(null)

// Saved search state
const currentSavedSearchId = ref(null)
const currentSavedSearchName = ref('')
const currentSavedSearchDescription = ref('')
const savedSearchQuery = ref('')      // Query at time of load/save
const savedSearchFilters = ref([])    // Filters at time of load/save
const saveLoading = ref(false)

// Subscription state
const isSubscribed = ref(false)
const subscriptionLoading = ref(false)

// Delete state
const deleteLoading = ref(false)

// Display log type state (which log type is shown in table)
const displayedLogType = ref(null)
const isUserSelection = ref(false)

// Column controller for the tool rail's Columns dropdown — scoped to the log type
// currently shown in the table. Mirrors the shape SelectorToolRail expects.
const columnController = computed(() => {
  const lt = displayedLogType.value
  const apiFields = lt && fieldMappings.value?.[lt]
  if (!apiFields || apiFields.length === 0) return null
  const ordered = getFieldOrder(lt, apiFields) // field-name strings, ordered
  return {
    scopeLabel: lt, // names the log type whose columns are being edited
    orderedFields: ordered,
    totalCount: ordered.length,
    enabledCount: ordered.filter(f => isFieldEnabled(lt, f)).length,
    hasCustom: hasCustomSettings(lt),
    labelFor: f => f, // event search uses the raw field name (matches ZeekLogsTable)
    isEnabled: f => isFieldEnabled(lt, f),
    toggle: f => toggleField(lt, f),
    setOrder: ids => setFieldOrder(lt, ids),
    reset: () => resetLogType(lt),
  }
})

// Table-specific state (separate from global overview)
const tableResults = ref([])
const tableTotal = ref(0)
const tableCurrentPage = ref(0)
const tableLoading = ref(false)
const tableMoreResults = ref(false)
const tableSort = ref([])  // [{field: "@timestamp", direction: "asc"}, ...]
const searchAfterHistory = ref([])  // cursor per page boundary for search_after pagination
const effectiveSort = computed(() =>
  tableSort.value.length > 0
    ? tableSort.value
    : [{ field: '@timestamp', direction: 'desc' }]
)
const searchGeneration = ref(0)
const tableSearchGeneration = ref(0)

// Filtered histogram: when a source is active, show only that source's log types
const filteredHistogram = computed(() => {
  if (!activeSource.value) return histogram.value
  const src = activeSource.value
  return histogram.value.map(bucket => ({
    ...bucket,
    counts: bucket.source_counts?.[src] || {},
  }))
})

// Source list scoped to the active source in source mode. All searches and
// aggregations triggered from a child view should use this so they stay
// consistent with the table contents.
const scopedSources = computed(() =>
  activeSource.value ? [dataSourcesStore.sourceToken(activeSource.value)] : dataSourcesStore.effectiveSources
)


// Load a saved search by ID
async function loadSavedSearch(savedId) {
  if (!savedId) return

  loading.value = true
  try {
    const response = await SavedSearches.get(savedId)
    if (response.status === 'not_found') {
      toast.error('Saved search not found')
      router.replace({ path: '/search/event' })
      return
    }
    if (response.status !== 'success') {
      toast.error('Failed to load saved search')
      router.replace({ path: '/search/event' })
      return
    }

    const search = response.data

    // Apply to state
    query.value = search.query
    lastExecutedQuery.value = search.query

    // Parse filters — separate time filters from non-time, merge non-time into query
    const parsedFilters = typeof search.filters === 'string'
      ? JSON.parse(search.filters)
      : search.filters
    const allFilters = parsedFilters || []
    const timeFilters = allFilters.filter(f => f.type === FILTER_TYPES.TIME_RANGE)
    const nonTimeFilters = allFilters.filter(f => f.type !== FILTER_TYPES.TIME_RANGE && f.enabled)

    // Convert non-time filters to Lucene text and merge into query string
    if (nonTimeFilters.length > 0) {
      const filterParts = nonTimeFilters
        .map(f => filterToQueryString(f))
        .filter(s => s.length > 0)
      if (filterParts.length > 0) {
        const filterText = filterParts.join(' AND ')
        const trimmed = query.value.trim()
        query.value = trimmed ? '(' + trimmed + ') AND ' + filterText : filterText
        lastExecutedQuery.value = query.value
      }
    }

    // Only keep time filters
    filters.value = timeFilters

    // Track as current saved search
    currentSavedSearchId.value = search.id
    currentSavedSearchName.value = search.name
    currentSavedSearchDescription.value = search.description || ''
    savedSearchQuery.value = lastExecutedQuery.value
    savedSearchFilters.value = []

    // Restore sources from saved search
    if (search.sources) {
      dataSourcesStore.deserializeSources(search.sources)
    } else {
      dataSourcesStore.resetSelection()
    }

    // Track subscription state
    isSubscribed.value = search.is_subscribed || false

    // Check for time filter params from route (e.g., "View New Results" button)
    // Supports both bounded (start + end) and open-ended (start only) time filters
    if (route.query.start) {
      const startMs = parseInt(route.query.start)
      const endMs = route.query.end ? parseInt(route.query.end) : null
      setTimeFilter(startMs, endMs)
    }

    // "New since last viewed" bound on the ingestion axis (notification links,
    // "View New Results" button) — filters event.ingested, not event.ts.
    if (route.query.ingestedSince) {
      setIngestSinceFilter(parseInt(route.query.ingestedSince))
    }

    // Append filter from URL if present (e.g., ?filter=field:value)
    if (route.query.filter) {
      applyUrlFilter(route.query.filter)
    }

    // Store logType from URL if present
    if (route.query.logType) {
      urlLogType.value = route.query.logType
    }

    // Mark search as "checked in" only if:
    // - No time/ingest filter (regular view), OR
    // - viewNew=true (explicitly viewing new results)
    // This prevents resetting the "new results" badge when just applying other filters
    const hasTimeFilter = !!route.query.start || !!route.query.ingestedSince
    const isViewingNewResults = route.query.viewNew === 'true'
    if (!hasTimeFilter || isViewingNewResults) {
      SavedSearches.checkIn(search.id)
    }

    // Execute search without logging (it's a reload of saved)
    await executeSearch(false, true)
  } catch (err) {
    console.error('Error loading saved search:', err)
    toast.error('Failed to load saved search')
    router.replace({ path: '/search/event' })
  } finally {
    loading.value = false
  }
}

// Watch for saved search ID in URL
watch(() => route.query.saved, async (savedId) => {
  if (savedId) {
    await loadSavedSearch(savedId)
  }
}, { immediate: false })

// Store URL-provided log type for setting after search
const urlLogType = ref(null)

// Initialize from route query params
onMounted(async () => {
  // Reset source selection to prevent stale state from prior sessions
  dataSourcesStore.resetSelection()

  // Init data sources store (fetches field mappings etc.) — must await so
  // serializedSources works correctly when syncUrlState runs during executeSearch
  await dataSourcesStore.init()

  // Check for saved search ID first
  // (logType and filter handling is done inside loadSavedSearch)
  if (route.query.saved) {
    await loadSavedSearch(route.query.saved)
    return
  }

  if (route.query.query) {
    query.value = route.query.query
    lastExecutedQuery.value = route.query.query  // Set stable base from route
  }
  // Supports both bounded (start + end) and open-ended (start only) time filters
  if (route.query.start) {
    const startMs = parseInt(route.query.start)
    const endMs = route.query.end ? parseInt(route.query.end) : null
    setTimeFilter(startMs, endMs)
    // Reflect in time range dropdown — preserve preset if passed from widget
    if (route.query.timePreset) {
      searchTimeRange.value = { type: 'preset', value: route.query.timePreset }
    } else {
      searchTimeRange.value = { type: 'custom', start: startMs, end: endMs }
    }
  }
  // "New since last viewed" bound on the ingestion axis (event.ingested)
  if (route.query.ingestedSince) {
    setIngestSinceFilter(parseInt(route.query.ingestedSince))
  }
  // Append filter from URL if present (e.g., ?filter=field:value)
  if (route.query.filter) {
    applyUrlFilter(route.query.filter)
  }
  // Store logType from URL if present
  if (route.query.logType) {
    urlLogType.value = route.query.logType
  }
  // Grouping mode from URL (e.g. a source drilldown from a selector view).
  // Set before executeSearch so the first request is already scoped — the
  // groupMode watcher is a no-op here, sourceLogTypeDistribution is still empty.
  if (isValidGroupMode(route.query.group)) {
    groupMode.value = route.query.group
  }
  // Restore source/log-type selections from URL
  restoreSourcesFromUrl(route.query.sources)

  // Execute search if query provided
  if (query.value) {
    const shouldLog = route.query.log !== 'false'
    executeSearch(shouldLog, true)
  }
})

// Parse sources URL param and apply to store
function restoreSourcesFromUrl(sourcesParam) {
  dataSourcesStore.deserializeSources(sourcesParam || '')
}

// Reset all search state (used when navigating back to landing)
function resetSearchState() {
  query.value = ''
  lastExecutedQuery.value = ''
  histogram.value = []
  logTypeDistribution.value = []
  sourceLogTypeDistribution.value = []
  activeSource.value = null
  tableResults.value = []
  tableTotal.value = 0
  tableCurrentPage.value = 0
  tableSort.value = []
  searchAfterHistory.value = []
  displayedLogType.value = null
  isUserSelection.value = false
  error.value = ''
  filters.value = []
  searchTimeRange.value = null
  currentSavedSearchId.value = null
  currentSavedSearchName.value = ''
  currentSavedSearchDescription.value = ''
  savedSearchQuery.value = ''
  savedSearchFilters.value = []
  isSubscribed.value = false
  dataSourcesStore.resetSelection()
}

// Build final query with all filters applied (uses stable lastExecutedQuery, not reactive input)
function buildQuery() {
  return buildFinalQuery(lastExecutedQuery.value, filters.value)
}

// Computed final query for child components (includes all filters)
const finalQuery = computed(() => buildFinalQuery(lastExecutedQuery.value, filters.value))

// Append a Lucene expression to the query string and re-execute. andJoin keeps
// the chain flat (`a AND b AND c`) and only parenthesizes operands that carry a
// top-level OR, instead of re-wrapping the whole query on every append.
function appendToQuery(expression) {
  query.value = andJoin(query.value, expression)
  lastExecutedQuery.value = query.value
  executeSearch(false)
}

// Parse filter from URL (format: "field:value") and append to query
function applyUrlFilter(filterString) {
  if (!filterString || typeof filterString !== 'string') return
  const colonIndex = filterString.indexOf(':')
  if (colonIndex === -1) return
  const field = filterString.substring(0, colonIndex).trim()
  const value = filterString.substring(colonIndex + 1).trim()
  if (!field || !value) return
  const expression = `${field}:"${value.replace(/"/g, '\\"')}"`
  query.value = andJoin(query.value, expression)
  lastExecutedQuery.value = query.value
}

// Filter CRUD operations (time filters only)
function removeFilter(id) {
  filters.value = filters.value.filter(f => f.id !== id)
}

function clearAllFilters() {
  filters.value = []
  searchTimeRange.value = null
}

// Update or create time filter
// endMs is optional - if null, creates an open-ended filter (no end bound)
function setTimeFilter(startMs, endMs = null) {
  const existingTimeFilter = findTimeFilter(filters.value)
  if (existingTimeFilter) {
    existingTimeFilter.values = { start: startMs, end: endMs }
    existingTimeFilter.enabled = true
  } else {
    filters.value.push(createTimeFilter(startMs, endMs))
  }
}

// Remove time filter
function removeTimeFilter() {
  const tf = findTimeFilter(filters.value)
  if (tf) {
    removeFilter(tf.id)
  }
}

// Update or create the "ingested since" filter (event.ingested lower bound).
// Used by the "view new results" flow — bounds the view on the same axis the
// notification count is measured on, so the two agree.
function setIngestSinceFilter(startMs) {
  const existing = findIngestFilter(filters.value)
  if (existing) {
    existing.values = { start: startMs }
    existing.enabled = true
  } else {
    filters.value.push(createIngestSinceFilter(startMs))
  }
}

// Remove the "ingested since" filter
function removeIngestFilter() {
  const inf = findIngestFilter(filters.value)
  if (inf) {
    removeFilter(inf.id)
  }
}

// Watermark (epoch ms) of the active "view new results" scope, or null when the
// view isn't ingestion-bounded. Drives the persistent NewResultsBanner strip.
const ingestSince = computed(() => findIngestFilter(filters.value)?.values.start ?? null)

// Leave "view new results" mode: drop the ingest bound and re-run (syncUrlState
// then strips ingestedSince from the URL).
function clearIngestFilter() {
  removeIngestFilter()
  executeSearch(false)
}

// Find the source with the highest count of the given log type.
// Falls back to the top source overall when the log type isn't anywhere.
function findTopSourceForLogType(logType) {
  if (sourceLogTypeDistribution.value.length === 0) return null
  if (logType) {
    let bestSource = null
    let bestCount = -1
    for (const src of sourceLogTypeDistribution.value) {
      const lt = src.log_types.find(l => l.log_type === logType)
      if (lt && lt.count > bestCount) {
        bestCount = lt.count
        bestSource = src.source
      }
    }
    if (bestSource) return bestSource
  }
  return sourceLogTypeDistribution.value[0].source
}

// Auto-select log type from distribution (pick the one with most results)
function autoSelectLogType() {
  if (logTypeDistribution.value.length === 0) {
    displayedLogType.value = null
    return null
  }

  // Sort by count descending, pick first
  const sorted = [...logTypeDistribution.value].sort((a, b) => b.count - a.count)
  displayedLogType.value = sorted[0].log_type
  return sorted[0].log_type
}

// Execute table-specific search (for the displayed log type)
async function executeTableSearch(logType, page = 0) {
  if (!logType || !query.value) {
    tableResults.value = []
    tableTotal.value = 0
    tableMoreResults.value = false
    return
  }

  const generation = ++tableSearchGeneration.value
  tableLoading.value = true
  tableCurrentPage.value = page

  try {
    const finalQuery = buildQuery()
    const sort = effectiveSort.value
    const cursor = page > 0 ? searchAfterHistory.value[page] || null : null
    const response = await Events.search(finalQuery, pageSize.value, 0, [logType], sort, scopedSources.value, cursor)

    if (generation !== tableSearchGeneration.value) return

    if (response.status === 'success') {
      tableResults.value = response.data.results || []
      tableTotal.value = response.data.total || 0

      // Store cursor for next page
      if (response.data.search_after) {
        searchAfterHistory.value[page + 1] = response.data.search_after
      }

      tableMoreResults.value = !!(response.data.search_after && (response.data.results || []).length === pageSize.value)
    } else if (response.status === 'timeout') {
      error.value = response.message || 'Query timeout - too many results. Try narrowing your time range or adding more specific filters.'
    } else if (response.status === 'error') {
      error.value = response.message || 'Invalid query syntax'
    }
  } catch (err) {
    if (generation !== tableSearchGeneration.value) return
    error.value = 'An unexpected error occurred'
    console.error('Table search error:', err)
  } finally {
    if (generation === tableSearchGeneration.value) {
      tableLoading.value = false
    }
  }
}

// URL state synchronization — pushes current search state to the URL for back/forward support
let skipRouteWatch = false

async function syncUrlState(replace = false) {
  const params = {}
  if (lastExecutedQuery.value) params.query = lastExecutedQuery.value
  if (currentSavedSearchId.value) params.saved = String(currentSavedSearchId.value)

  // Time filter params
  const tf = findTimeFilter(filters.value)
  if (tf?.enabled) {
    params.start = String(tf.values.start)
    if (tf.values.end != null) params.end = String(tf.values.end)
  }
  if (searchTimeRange.value?.type === 'preset') {
    params.timePreset = searchTimeRange.value.value
  }

  // "Ingested since" (view-new) bound — persisted so it survives table<->agg
  // switches and back/forward. viewNew is intentionally NOT persisted: its only
  // effect is the one-time check-in on the initial click.
  const inf = findIngestFilter(filters.value)
  if (inf?.enabled) {
    params.ingestedSince = String(inf.values.start)
  }

  // Source / log-type selection params (omitted when "all")
  const sources = dataSourcesStore.serializedSources
  if (sources) {
    params.sources = sources
  }

  // Grouping mode (omitted at the default, like the source params above)
  if (groupMode.value !== DEFAULT_GROUP_MODE) {
    params.group = groupMode.value
  }

  // Ensure we navigate to results view when executing a search
  const path = lastExecutedQuery.value
    ? (route.path === '/search/event' ? '/search/event/table' : route.path)
    : route.path

  skipRouteWatch = true
  try {
    const nav = { path, query: params }
    if (replace) await router.replace(nav)
    else await router.push(nav)
    await nextTick()
  } finally {
    skipRouteWatch = false
  }
}

// Re-sync URL state when switching between child views (table <-> aggregations)
// The layout owns the search state, so it re-pushes params onto the new path.
// When navigating to the landing page, clear all state instead.
watch(() => route.path, (newPath, oldPath) => {
  if (skipRouteWatch) return
  if (newPath === oldPath) return
  // Only act when still within the event search layout
  if (!newPath.startsWith('/search/event')) return

  // Navigating to landing — clear all search state
  if (newPath === '/search/event') {
    resetSearchState()
    return
  }

  nextTick(() => syncUrlState(true))
})

// Restore state on back/forward navigation
watch(() => route.query, (newQuery, oldQuery) => {
  if (skipRouteWatch) return
  if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return
  if (newQuery.saved) return // Saved searches handled by their own watcher

  // No query in URL — reset everything (e.g. browser back to landing)
  if (!newQuery.query) {
    resetSearchState()
    return
  }

  query.value = newQuery.query
  lastExecutedQuery.value = newQuery.query

  // Restore time filter
  removeTimeFilter()
  searchTimeRange.value = null
  if (newQuery.start) {
    const startMs = parseInt(newQuery.start)
    const endMs = newQuery.end ? parseInt(newQuery.end) : null
    setTimeFilter(startMs, endMs)
    searchTimeRange.value = newQuery.timePreset
      ? { type: 'preset', value: newQuery.timePreset }
      : { type: 'custom', start: startMs, end: endMs }
  }

  // Restore "ingested since" (view-new) filter
  removeIngestFilter()
  if (newQuery.ingestedSince) {
    setIngestSinceFilter(parseInt(newQuery.ingestedSince))
  }

  // Restore source/log-type selections
  if (newQuery.sources) {
    restoreSourcesFromUrl(newQuery.sources)
  } else {
    dataSourcesStore.resetSelection()
  }

  // Restore grouping mode — assigned unconditionally so navigating back to a URL
  // without the param returns to the default rather than keeping the old mode
  groupMode.value = isValidGroupMode(newQuery.group) ? newQuery.group : DEFAULT_GROUP_MODE

  executeSearch(false, true)
}, { deep: true })

// Execute global search (for histogram and log type distribution)
async function executeSearch(shouldLog = true, replace = false) {
  if (!hasEventAccess.value) {
    forbidden.value = true
    loading.value = false
    return
  }

  const generation = ++searchGeneration.value

  // Extract inline @timestamp:[...] from query — normalizes time into the filter system
  // regardless of entry point (typed query, history, URL)
  const { cleanQuery, start: tsStart, end: tsEnd } = extractTimeFromQuery(query.value)
  if (tsStart !== null) {
    query.value = cleanQuery
    lastExecutedQuery.value = cleanQuery
    setTimeFilter(tsStart, tsEnd)
    searchTimeRange.value = { type: 'custom', start: tsStart, end: tsEnd }
  }

  // When executing a new search (shouldLog=true), update the stable base query
  if (shouldLog) {
    lastExecutedQuery.value = query.value
  }

  const finalQuery = buildQuery()

  // Sync current state to URL for back/forward navigation
  await syncUrlState(replace)

  // Log the query to history
  if (shouldLog && lastExecutedQuery.value) {
    await User.save_query([finalQuery], 'event_search', dataSourcesStore.serializedSources || null)
    userStore.getQueries('event')
  }

  // Reset state
  error.value = ''
  loading.value = true

  // Clear previous data
  histogram.value = []
  logTypeDistribution.value = []
  sourceLogTypeDistribution.value = []
  tableResults.value = []
  tableTotal.value = 0
  tableCurrentPage.value = 0
  searchAfterHistory.value = []

  // Reset user selection on new search query (shouldLog=true means new query)
  if (shouldLog) {
    displayedLogType.value = null
    isUserSelection.value = false
    activeSource.value = null
  }

  // Pass the active time-range filter as time_range so the histogram agg
  // bounds the x-axis to the user's selection (empty buckets render as zeros)
  // rather than collapsing to the data's actual range.
  const tf = findTimeFilter(filters.value)
  const histogramRange = tf ? tf.values : null

  try {
    // Execute global search (filtered by source/log selection) for overview data
    const response = await Events.search(finalQuery, 1, 0, dataSourcesStore.effectiveLogTypes, [], dataSourcesStore.effectiveSources, null, histogramRange)

    if (generation !== searchGeneration.value) return

    if (response.status === 'success') {
      // Store overview data
      histogram.value = response.data.histogram || []
      logTypeDistribution.value = response.data.log_types || []
      sourceLogTypeDistribution.value = response.data.source_log_types || []

      // Determine which log type to display
      let selectedLogType = null

      // Check if URL provided a log type
      if (urlLogType.value) {
        const urlLogTypeData = logTypeDistribution.value.find(
          lt => lt.log_type === urlLogType.value
        )
        if (urlLogTypeData && urlLogTypeData.count > 0) {
          // Use URL-provided log type if it has results
          selectedLogType = urlLogType.value
          displayedLogType.value = urlLogType.value
          isUserSelection.value = true
          urlLogType.value = null  // Clear after use
        }
      }

      // If no URL log type or it didn't have results, use existing logic
      if (!selectedLogType) {
        if (isUserSelection.value && displayedLogType.value) {
          // Check if user's selection still has results
          const userLogTypeData = logTypeDistribution.value.find(
            lt => lt.log_type === displayedLogType.value
          )

          if (userLogTypeData && userLogTypeData.count > 0) {
            // Keep user's selection - it still has results
            selectedLogType = displayedLogType.value
          } else {
            // User's selection has no results - fall back to auto-select
            isUserSelection.value = false
            selectedLogType = autoSelectLogType()
          }
        } else {
          // No user selection - auto-select
          selectedLogType = autoSelectLogType()
        }
      }

      // In source mode, ensure an activeSource is set so the table is scoped
      // (and the sidebar's nested rows highlight correctly).
      if (groupMode.value === 'source' && !activeSource.value) {
        const topSource = findTopSourceForLogType(selectedLogType)
        if (topSource) {
          activeSource.value = topSource
          // If the chosen source doesn't carry the picked log type, swap to its top.
          const sourceEntry = sourceLogTypeDistribution.value.find(s => s.source === topSource)
          if (sourceEntry?.log_types.length > 0) {
            const inSource = sourceEntry.log_types.some(lt => lt.log_type === selectedLogType)
            if (!inSource) {
              selectedLogType = sourceEntry.log_types[0].log_type
              displayedLogType.value = selectedLogType
            }
          }
        }
      }

      if (selectedLogType) {
        await executeTableSearch(selectedLogType)
      }
    } else if (response.status === 'timeout') {
      error.value = response.message || 'Query timeout - too many results. Try narrowing your time range or adding more specific filters.'
    } else if (response.status === 'error') {
      error.value = response.message || 'Invalid query syntax'
    } else if (response.status === 'ratelimited') {
      error.value = 'Too many requests - please wait and try again'
    } else if (response.status === 'unauthorized') {
      error.value = 'Unauthorized - admin access required'
    } else {
      error.value = 'Search failed - please try again'
    }
  } catch (err) {
    if (generation !== searchGeneration.value) return
    error.value = 'An unexpected error occurred'
    console.error('Search error:', err)
  } finally {
    if (generation === searchGeneration.value) {
      loading.value = false
    }
  }
}

// Handle log type selection from sidebar
function handleLogTypeSelect(logType) {
  if (displayedLogType.value === logType) return

  displayedLogType.value = logType
  isUserSelection.value = true
  tableCurrentPage.value = 0
  tableSort.value = []  // Reset sort when changing log type
  searchAfterHistory.value = []
  executeTableSearch(logType)
}

// Refetch the table with the current activeSource + displayedLogType.
function refetchTable() {
  tableCurrentPage.value = 0
  tableSort.value = []
  searchAfterHistory.value = []
  if (displayedLogType.value) {
    executeTableSearch(displayedLogType.value)
  }
}

// Handle source selection from sidebar.
// Source mode always has an activeSource — clicking the same source is a no-op.
// Clicking a different source scopes the timeline+table; the log stays if the
// source carries it, otherwise switches to that source's top log.
function handleSourceSelect(sourceName) {
  if (activeSource.value === sourceName) return

  activeSource.value = sourceName

  const sourceEntry = sourceLogTypeDistribution.value.find(s => s.source === sourceName)
  if (sourceEntry && sourceEntry.log_types.length > 0) {
    const currentInSource = sourceEntry.log_types.some(lt => lt.log_type === displayedLogType.value)
    if (!currentInSource) {
      displayedLogType.value = sourceEntry.log_types[0].log_type
      isUserSelection.value = true
    }
  }

  refetchTable()
}

// Mode toggle:
//   → source: auto-pick the top source for the current log so source mode
//     never has a null activeSource.
//   → logtype: clear activeSource so the global view comes back.
watch(groupMode, (mode) => {
  if (mode === 'source') {
    if (activeSource.value) return
    if (sourceLogTypeDistribution.value.length === 0) return
    const topSource = findTopSourceForLogType(displayedLogType.value)
    if (topSource) handleSourceSelect(topSource)
  } else if (activeSource.value) {
    activeSource.value = null
    refetchTable()
  }
})

// Handle atomic source+log-type selection from a nested row in an expanded source
function handleSourceLogTypeSelect({ source, logType }) {
  const sourceChanged = activeSource.value !== source
  const logTypeChanged = displayedLogType.value !== logType
  if (!sourceChanged && !logTypeChanged) return

  activeSource.value = source
  displayedLogType.value = logType
  isUserSelection.value = true
  tableCurrentPage.value = 0
  tableSort.value = []
  searchAfterHistory.value = []
  executeTableSearch(logType)
}

// Navigate to next page (table)
async function tableNextPage() {
  if (!tableMoreResults.value || tableLoading.value) return
  await executeTableSearch(displayedLogType.value, tableCurrentPage.value + 1)
}

// Navigate to previous page (table)
async function tablePrevPage() {
  if (tableCurrentPage.value === 0 || tableLoading.value) return
  await executeTableSearch(displayedLogType.value, tableCurrentPage.value - 1)
}

// Change page size
function handlePageSizeChange(size) {
  pageSize.value = size
  localStorage.setItem('eventSearchPageSize', size)
  tableCurrentPage.value = 0
  searchAfterHistory.value = []
  if (displayedLogType.value) {
    executeTableSearch(displayedLogType.value, 0)
  }
}

// Check if export exceeds 10k limit
function exportExceedsLimit(type, logType) {
  if (type === 'logType' && logType) {
    const dist = logTypeDistribution.value.find(d => d.log_type === logType)
    return dist && dist.count > 10000
  }
  // "all" — check total across all log types
  const total = logTypeDistribution.value.reduce((sum, d) => sum + d.count, 0)
  return total > 10000
}

// Initiate export with warning dialogs if needed
function exportResults(format = 'jsonl') {
  startExportWithWarnings({ type: 'all', format })
}

function exportLogType(logType, format = 'jsonl') {
  startExportWithWarnings({ type: 'logType', logType, format })
}

function startExportWithWarnings(exportInfo) {
  pendingExport.value = exportInfo

  // Check CSV warning first
  if (exportInfo.format === 'csv' && localStorage.getItem(CSV_SAFETY_KEY) !== 'true') {
    showCsvWarning.value = true
    return
  }

  // Then check partial results warning
  if (exportExceedsLimit(exportInfo.type, exportInfo.logType) && localStorage.getItem(PARTIAL_EXPORT_KEY) !== 'true') {
    showPartialWarning.value = true
    return
  }

  // No warnings needed, proceed
  executeExport(exportInfo)
}

function onCsvWarningConfirm() {
  showCsvWarning.value = false
  const exportInfo = pendingExport.value
  if (!exportInfo) return

  // Check if partial warning also needed
  if (exportExceedsLimit(exportInfo.type, exportInfo.logType) && localStorage.getItem(PARTIAL_EXPORT_KEY) !== 'true') {
    showPartialWarning.value = true
    return
  }

  executeExport(exportInfo)
}

function onPartialWarningConfirm() {
  showPartialWarning.value = false
  const exportInfo = pendingExport.value
  if (!exportInfo) return
  executeExport(exportInfo)
}

function onExportWarningCancel() {
  showCsvWarning.value = false
  showPartialWarning.value = false
  pendingExport.value = null
}

// Execute the actual export API call
async function executeExport(exportInfo) {
  pendingExport.value = null
  exportLoading.value = true
  error.value = ''

  try {
    const finalQuery = buildFinalQuery(lastExecutedQuery.value, filters.value)
    const logTypes = exportInfo.type === 'logType' ? [exportInfo.logType] : dataSourcesStore.effectiveLogTypes
    const label = exportInfo.type === 'logType' ? `Export started for ${exportInfo.logType}` : 'Export started - download will begin shortly'
    toast.success(label)
    const response = await Events.exportDocuments(finalQuery, logTypes, exportInfo.format, dataSourcesStore.effectiveSources)

    if (response.status === 'ratelimited') {
      toast.error('Too many requests - please wait and try again')
    } else if (response.status === 'unauthorized') {
      toast.error('Unauthorized - admin access required')
    } else if (response.status === 'timeout') {
      toast.error('Export timeout - too many results. Try narrowing your query.')
    } else if (response.status !== 'success') {
      toast.error('Export failed - please try again')
    }
  } catch (err) {
    toast.error('Export failed')
    console.error('Export error:', err)
  } finally {
    exportLoading.value = false
  }
}

// Save as new search
async function handleSaveSearch({ name, description }) {
  saveLoading.value = true
  try {
    const response = await SavedSearches.create(
      name,
      description,
      lastExecutedQuery.value,
      [],
      null,
      dataSourcesStore.serializedSources || null
    )
    if (response.status === 'success') {
      currentSavedSearchId.value = response.data.id
      currentSavedSearchName.value = name
      currentSavedSearchDescription.value = description
      savedSearchQuery.value = lastExecutedQuery.value
      savedSearchFilters.value = []
      isSubscribed.value = false
      toast.success('Search saved successfully')
    } else {
      toast.error(response.message || 'Failed to save search')
    }
  } catch (err) {
    toast.error('Failed to save search')
    console.error('Save search error:', err)
  } finally {
    saveLoading.value = false
  }
}

// Subscribe to search updates
async function handleSubscribe() {
  if (!currentSavedSearchId.value) return

  subscriptionLoading.value = true
  try {
    const response = await SavedSearches.subscribe(currentSavedSearchId.value)
    if (response.status === 'success') {
      isSubscribed.value = true
      toast.success('Subscribed to search updates')
    } else {
      toast.error(response.message || 'Failed to subscribe')
    }
  } catch (err) {
    toast.error('Failed to subscribe')
    console.error('Subscribe error:', err)
  } finally {
    subscriptionLoading.value = false
  }
}

// Unsubscribe from search updates
async function handleUnsubscribe() {
  if (!currentSavedSearchId.value) return

  subscriptionLoading.value = true
  try {
    const response = await SavedSearches.unsubscribe(currentSavedSearchId.value)
    if (response.status === 'success') {
      isSubscribed.value = false
      toast.success('Unsubscribed from search updates')
    } else {
      toast.error(response.message || 'Failed to unsubscribe')
    }
  } catch (err) {
    toast.error('Failed to unsubscribe')
    console.error('Unsubscribe error:', err)
  } finally {
    subscriptionLoading.value = false
  }
}

// Overwrite existing search
async function handleOverwriteSearch({ name, description }) {
  if (!currentSavedSearchId.value) return

  saveLoading.value = true
  try {
    const response = await SavedSearches.update(
      currentSavedSearchId.value,
      name,
      description,
      lastExecutedQuery.value,
      [],
      dataSourcesStore.serializedSources || null
    )
    if (response.status === 'success') {
      currentSavedSearchName.value = name
      currentSavedSearchDescription.value = description
      savedSearchQuery.value = lastExecutedQuery.value
      savedSearchFilters.value = []
      toast.success('Search updated successfully')
    } else {
      toast.error(response.message || 'Failed to update search')
    }
  } catch (err) {
    toast.error('Failed to update search')
    console.error('Update search error:', err)
  } finally {
    saveLoading.value = false
  }
}

// Delete saved search
async function handleDeleteSearch() {
  if (!currentSavedSearchId.value) return

  deleteLoading.value = true
  try {
    const response = await SavedSearches.delete(currentSavedSearchId.value)
    if (response.status === 'success') {
      // Clear saved search state (keep query and filters)
      currentSavedSearchId.value = null
      currentSavedSearchName.value = ''
      currentSavedSearchDescription.value = ''
      savedSearchQuery.value = ''
      savedSearchFilters.value = []
      isSubscribed.value = false
      // Remove the saved query param from URL
      router.replace({ path: '/search/event' })
      toast.success('Search deleted')
    } else {
      toast.error(response.message || 'Failed to delete search')
    }
  } catch (err) {
    toast.error('Failed to delete search')
    console.error('Delete search error:', err)
  } finally {
    deleteLoading.value = false
  }
}

// Handle time filter updates from child (histogram selection)
function handleTimeFilterUpdate(newFilter) {
  setTimeFilter(newFilter.start, newFilter.end)
  searchTimeRange.value = { type: 'custom', start: newFilter.start, end: newFilter.end }
  executeSearch(false)
}

// Handle time range dropdown selection
function handleTimeRangeChange(selection) {
  searchTimeRange.value = selection
  const params = selectionToFilterParams(selection)
  if (params) {
    setTimeFilter(params.start, params.end)
  } else {
    removeTimeFilter()
  }
  // Only re-execute if there's an active search (lastExecutedQuery set)
  if (lastExecutedQuery.value.trim()) {
    executeSearch(false, true)
  }
}

// Handle search from search bar. A hand-run query invalidates the click-filter
// tracking (the user may have edited the text).
function handleSearch() {
  clickFilters.value = []
  executeSearch(true)
}

// buildFilterClause + stripClauseFromQuery are shared with the selector filter
// bar; both are imported from @/components/EventSearch/filters.

// Handle filter application from table cells (+/- and bulk operators).
// Reconcile against what a prior click appended (tracked by the item set's
// signature) so an include→negate on the same value(s) doesn't produce an
// always-empty `field:"x" AND NOT field:"x"`:
//   same operator again → strip the clause (toggle off)
//   opposite operator   → strip the old clause, append the negated one (flip)
function handleApplyFilter(queueItems, operator) {
  const sig = queueItems.map(i => i.valueKey).sort().join('|')
  const existing = clickFilters.value.find(c => c.sig === sig)

  if (existing) {
    query.value = stripClauseFromQuery(query.value, existing.clause)
    clickFilters.value = clickFilters.value.filter(c => c !== existing)
    if (existing.operator === operator) {
      // Toggle off — the strip is the whole change.
      lastExecutedQuery.value = query.value
      executeSearch(false)
      return
    }
    // Flip: fall through to append the opposite-operator clause onto the
    // now-stripped query (appendToQuery re-syncs lastExecutedQuery + executes).
  }

  const clause = buildFilterClause(queueItems, operator)
  if (clause) {
    appendToQuery(clause)
    clickFilters.value.push({ sig, operator, clause })
  }
}

// Handle single filter addition from column stats popover
function handleAddSingleFilter(filterData) {
  // filterData: { field, value, operator }
  if (filterData.operator === 'time_range') {
    const ts = Number(filterData.value)
    if (!isNaN(ts)) {
      setTimeFilter(ts, null)
    }
    return
  }
  const value = String(filterData.value).replace(/"/g, '\\"')
  const clause = `${filterData.field}:"${value}"`
  appendToQuery(filterData.operator === 'exclude' ? `NOT ${clause}` : clause)
}

// Sortable field types in Elasticsearch
const SORTABLE_TYPES = new Set([
  'keyword', 'date', 'long', 'integer', 'short', 'byte',
  'double', 'float', 'half_float', 'scaled_float', 'boolean', 'ip'
])

// Handle sort change from table header click
// fieldInfo: { type: string, hasKeyword: boolean }
function handleSortChange(field, fieldInfo) {
  const { type: fieldType, hasKeyword } = fieldInfo

  // Check if field is sortable (native sortable type OR text with keyword sub-field)
  const isSortable = SORTABLE_TYPES.has(fieldType) || (fieldType === 'text' && hasKeyword)

  if (!isSortable) {
    toast.error(`Cannot sort by "${field}" - ${fieldType} fields are not sortable`)
    return
  }

  // Determine if we need to use .keyword suffix
  const useKeyword = fieldType === 'text' && hasKeyword

  const existingIndex = tableSort.value.findIndex(s => s.field === field)

  if (existingIndex === -1) {
    // First click: add ascending
    tableSort.value = [...tableSort.value, { field, direction: 'asc', use_keyword: useKeyword }]
  } else if (tableSort.value[existingIndex].direction === 'asc') {
    // Second click: change to descending
    tableSort.value = tableSort.value.map((s, i) =>
      i === existingIndex ? { ...s, direction: 'desc' } : s
    )
  } else {
    // Third click: remove
    tableSort.value = tableSort.value.filter((_, i) => i !== existingIndex)
  }

  // Re-fetch table data from page 1
  tableCurrentPage.value = 0
  searchAfterHistory.value = []
  executeTableSearch(displayedLogType.value, 0)
}

// Handle a search run from the landing card. The card's own SourceSelector has
// already written the selection to the store, so — unlike a history re-run — we
// leave sources alone and only fold in the card's time range.
async function handleLandingSearch(queryText, timeSelection) {
  query.value = queryText
  searchTimeRange.value = timeSelection
  const params = selectionToFilterParams(timeSelection)
  if (params) {
    setTimeFilter(params.start, params.end)
  } else {
    removeTimeFilter()
  }

  skipRouteWatch = true
  await router.push({ path: '/search/event/table' })
  await nextTick()
  skipRouteWatch = false
  executeSearch(true, true)
}

// Handle re-running a stored query from the history view or the landing's
// History tab. The stored string carries its own @timestamp:[…] clause, which
// executeSearch extracts back into the filter system — so there's no time
// argument here, only the sources the query was run against.
async function handleHistoryQuerySelect(queryText, sources) {
  query.value = queryText
  if (sources) {
    dataSourcesStore.deserializeSources(sources)
  } else {
    dataSourcesStore.resetSelection()
  }
  skipRouteWatch = true
  await router.push({ path: '/search/event/table' })
  await nextTick()
  skipRouteWatch = false
  executeSearch(true, true)
}

// Handle log type exploration from landing page (exclusively select log type, 7d window)
async function handleExploreLogType(logType) {
  dataSourcesStore.selectExclusiveLogType(logType)
  query.value = '*'
  const now = Date.now()
  const start = now - 7 * 24 * 60 * 60 * 1000
  setTimeFilter(start, null)
  searchTimeRange.value = { type: 'preset', value: '7d' }

  skipRouteWatch = true
  await router.push({ path: '/search/event/table' })
  await nextTick()
  skipRouteWatch = false
  executeSearch(true, true)
}

// Handle source exploration from landing page (exclusively select source, 7d window)
async function handleExploreSource(sourceId) {
  dataSourcesStore.selectExclusiveSource(sourceId)
  query.value = '*'
  const now = Date.now()
  const start = now - 7 * 24 * 60 * 60 * 1000
  setTimeFilter(start, null)
  searchTimeRange.value = { type: 'preset', value: '7d' }

  skipRouteWatch = true
  await router.push({ path: '/search/event/table' })
  await nextTick()
  skipRouteWatch = false
  executeSearch(true, true)
}

// Handle navigation to aggregations view (from column stats popover).
// Suppress the route watchers: the layout's state is already current, and
// letting them run would reset source selection and strip `field` from the
// URL before AggregationsView reads it.
async function handleNavigateAggregations(field) {
  const params = { field }
  if (lastExecutedQuery.value) params.query = lastExecutedQuery.value

  const tf = findTimeFilter(filters.value)
  if (tf?.enabled) {
    params.start = String(tf.values.start)
    if (tf.values.end != null) params.end = String(tf.values.end)
  }
  if (searchTimeRange.value?.type === 'preset') {
    params.timePreset = searchTimeRange.value.value
  }
  // Carry the "ingested since" bound too. This handler builds params from
  // scratch rather than going through syncUrlState, so without this the jump to
  // aggregations silently widens a "view new results" session back to all time.
  const inf = findIngestFilter(filters.value)
  if (inf?.enabled) {
    params.ingestedSince = String(inf.values.start)
  }

  skipRouteWatch = true
  try {
    await router.push({ name: 'EventSearchAggregations', query: params })
    await nextTick()
  } finally {
    skipRouteWatch = false
  }
}
</script>

<template>
  <div class="event-search-layout">
    <!-- Top Search Bar -->
    <EventSearchBar
      v-if="!isLanding"
      v-model="query"
      :loading="loading"
      :time-range="searchTimeRange"
      @search="handleSearch"
      @time-range-change="handleTimeRangeChange"
    />

    <!-- "View new results" context strip — sits directly under the search bar
         while the view is scoped to newly-ingested results. -->
    <NewResultsBanner
      v-if="!isLanding && ingestSince !== null"
      :since="ingestSince"
      @clear="clearIngestFilter"
    />

    <!-- Main Content Area -->
    <div class="event-search-main">
      <!-- Content (router-view for nested routes) -->
      <div class="event-search-content" :class="{ 'is-landing': isLanding }">
        <router-view
          :query="finalQuery"
          :base-query="lastExecutedQuery"
          :histogram="filteredHistogram"
          :log-type-distribution="logTypeDistribution"
          :source-log-type-distribution="sourceLogTypeDistribution"
          :displayed-log-type="displayedLogType"
          :active-source="activeSource"
          :loading="loading"
          :error="error"
          :forbidden="forbidden"
          :table-results="tableResults"
          :table-total="tableTotal"
          :table-current-page="tableCurrentPage"
          :table-loading="tableLoading"
          :table-more-results="tableMoreResults"
          :table-sort="effectiveSort"
          :page-size="pageSize"
          :field-mappings="fieldMappings"
          :is-field-enabled="isFieldEnabled"
          :toggle-field="toggleField"
          :get-field-order="getFieldOrder"
          :set-field-order="setFieldOrder"
          :get-ordered-enabled-fields="getOrderedEnabledFields"
          :reset-log-type="resetLogType"
          :has-custom-settings="hasCustomSettings"
          :sources="scopedSources"
          @update:time-filter="handleTimeFilterUpdate"
          @select-log-type="handleLogTypeSelect"
          @select-source="handleSourceSelect"
          @select-source-log-type="handleSourceLogTypeSelect"
          @table-next-page="tableNextPage"
          @table-prev-page="tablePrevPage"
          @apply-filter="handleApplyFilter"
          @sort-change="handleSortChange"
          @add-filter="handleAddSingleFilter"
          @update:page-size="handlePageSizeChange"
          @navigate-aggregations="handleNavigateAggregations"
          @run-search="handleLandingSearch"
          @execute-query="handleHistoryQuerySelect"
          @explore-log-type="handleExploreLogType"
          @explore-source="handleExploreSource"
        />
      </div>

      <!-- Right Tool Sidebar -->
      <ToolSidebar
        v-if="!isLanding"
        :has-executed-query="!!lastExecutedQuery"
        :can-export="tableResults.length > 0 || logTypeDistribution.length > 0"
        :loading="loading"
        :export-loading="exportLoading"
        :log-type-distribution="logTypeDistribution"
        :columns="columnController"
        :has-geo-annotations="dataSourcesStore.hasGeoAnnotations"
        :saved-search-id="currentSavedSearchId"
        :saved-search-name="currentSavedSearchName"
        :saved-search-description="currentSavedSearchDescription"
        :saved-search-query="savedSearchQuery"
        :current-query="lastExecutedQuery"
        :save-loading="saveLoading"
        :delete-loading="deleteLoading"
        :is-subscribed="isSubscribed"
        :subscription-loading="subscriptionLoading"
        @update:expanded="toolSidebarExpanded = $event"
        @export="exportResults"
        @export-log-type="exportLogType"
        @save-search="handleSaveSearch"
        @overwrite-search="handleOverwriteSearch"
        @delete-search="handleDeleteSearch"
        @subscribe="handleSubscribe"
        @unsubscribe="handleUnsubscribe"
      />
    </div>

    <!-- CSV Safety Warning -->
    <ExportWarningModal
      :show="showCsvWarning"
      :icon="['fas', 'shield-halved']"
      title="CSV Export Safety Notice"
      :storage-key="CSV_SAFETY_KEY"
      @confirm="onCsvWarningConfirm"
      @cancel="onExportWarningCancel"
    >
      <p>Export data should be treated as <strong>untrusted</strong>. When opening CSV files in spreadsheet programs (Excel, Google Sheets, LibreOffice Calc), <strong>disable automatic formula execution</strong> to prevent potential security risks.</p>
      <p>Values starting with <code>=</code> <code>+</code> <code>-</code> <code>@</code> or <code>|</code> are prefixed with <code>[PROT]</code> to mitigate most formula injection attacks.</p>
    </ExportWarningModal>

    <!-- Partial Export Warning -->
    <ExportWarningModal
      :show="showPartialWarning"
      :icon="['fas', 'triangle-exclamation']"
      title="Partial Export Warning"
      :storage-key="PARTIAL_EXPORT_KEY"
      @confirm="onPartialWarningConfirm"
      @cancel="onExportWarningCancel"
    >
      <p>Your query matches more than <strong>10,000</strong> documents. The export will contain only the <strong>first 10,000 results</strong>.</p>
      <p>Consider narrowing your query or filtering by log type to get more complete results.</p>
    </ExportWarningModal>
  </div>
</template>

<style scoped>
.event-search-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

.event-search-main {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.event-search-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 1rem;
  padding-left: 0;
  padding-bottom: 0;
  overflow: hidden;
}

/* The landing stage centers itself in whatever box it's given, so the inset
   above — asymmetric to sit the content against the rail — would throw it
   off-center. */
.event-search-content.is-landing {
  padding: 0;
}

.event-search-content :deep(> *) {
  flex: 1;
  min-height: 0;
}

/* Match the content's top inset so the rail isn't flush against the search bar. */
.event-search-main :deep(.rail-wrapper) {
  padding-top: 1rem;
}
</style>
